<template>
  <div class="sign-in-container" data-qa="LOGIN_PAGE">
    <div class="language-selector">
      <LanguageMenu
        v-model="uiLocale"
        :options="locales"
        position="bottom-right"
        :has-arrow="false"
        data-qa="LOGIN_LANGUAGE"
      />
    </div>
    <div class="sign-in-middle-area">
      <div class="lemonway-logo">
        <SvgIcon value="LemonwayLogoWhite" data-qa="LOGIN_LOGO_IMG" />
      </div>
      <LoginAnimated
        v-if="!isLocaleReady"
        class="sign-in-animated-logo"
        data-qa="LOGIN_LOCALE_LOADING"
      />
      <div v-else class="sign-in-slot">
        <slot />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { LanguageMenu, LoginAnimated, SvgIcon } from '@lw-design-system/ui';

const { locale, locales, getLocaleCookie, setLocale, setLocaleCookie } = useI18n();

const isLocaleReady = ref(false);
const appliedLocale = ref<string>(locale.value);
const uiLocale = ref<string>(locale.value);

onMounted(async () => {
  const cookie = getLocaleCookie();
  const initialLocale = cookie && isSupportedLocale(cookie) ? cookie : locale.value;
  appliedLocale.value = initialLocale;
  uiLocale.value = initialLocale;
  try {
    await setLocale(initialLocale);
  } finally {
    isLocaleReady.value = true;
  }
});

watch(locale, (newLocale: string) => {
  if (uiLocale.value !== newLocale) uiLocale.value = newLocale;
});

watch(uiLocale, async (selectedLocale: string) => {
  if (!isSupportedLocale(selectedLocale)) return;
  if (selectedLocale === appliedLocale.value) return;
  appliedLocale.value = selectedLocale;
  await setLocale(selectedLocale);
  setLocaleCookie(selectedLocale);
});
</script>
<style scoped>
.sign-in-container {
  background: var(
    --Login-gradient,
    radial-gradient(129.67% 70.68% at 50% 50.04%, #365a8b 0%, #1e324d 100%)
  );
  min-height: 100vh;
  padding: 1.5rem;
}

.language-selector {
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
}

.sign-in-middle-area {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 3rem;
  width: 100%;
}

.lemonway-logo {
  width: 16.25rem;
  height: 3.75rem;
}

.sign-in-slot {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

@media only screen and (max-width: 47.9375rem) {
  .sign-in-container {
    padding: 1.25rem;
  }

  .language-selector {
    position: absolute;
    top: 0.625rem;
    right: 0.625rem;
  }

  .sign-in-middle-area {
    margin-top: 3rem;
  }
}

@media only screen and (min-width: 48rem) {
  .sign-in-container {
    display: flex;
  }
}
</style>
